import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Grid } from './ui';
import useMediaQuery from '../hooks/useMediaQuery';
import axios from 'axios';
import { Navigate, Route, Routes } from 'react-router-dom';
import DownloadNew from './DownloadManager/DownloadNew';
import DownloadProgress from './DownloadManager/DownloadProgress';
import DownloadHistory from './DownloadManager/DownloadHistory';
import WebSocketContext from '../contexts/WebSocketContext';
import { useDownloadListingsRefresh } from '../hooks/useDownloadListingsRefresh';
import { Job } from '../types/Job';

interface DownloadManagerProps {
  token: string | null;
}

function DownloadManager({ token }: DownloadManagerProps) {
  const [videoUrls, setVideoUrls] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const downloadInitiatedRef = useRef(false);
  const downloadProgressRef = useRef<{ index: number | null; message: string }>(
    { index: null, message: '' }
  );
  const wsContext = useContext(WebSocketContext);
  if (!wsContext) {
    throw new Error('WebSocketContext not found');
  }

  const isMobile = useMediaQuery('(max-width: 599px)');

  const fetchRunningJobs = useCallback(() => {
    if (token) {
      axios
        .get('/runningjobs', {
          headers: {
            'x-access-token': token,
          },
        })
        .then((response) => {
          if (response.data) {
            setJobs(response.data);
          }
        });
    }
  }, [token]);

  useEffect(() => {
    fetchRunningJobs();
  }, [fetchRunningJobs]);

  useDownloadListingsRefresh(fetchRunningJobs);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer); // Clean up the interval on unmount
  }, []);

  // This function toggles the expanded state for a single job.
  const handleExpandCell = (id: string) => {
    setExpanded((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  return (
    <Routes>
      <Route index element={<Navigate to="manual" replace />} />
      <Route
        path="manual"
        element={
          <Grid container spacing={2}>
            <DownloadNew
              videoUrls={videoUrls}
              setVideoUrls={setVideoUrls}
              token={token}
              fetchRunningJobs={fetchRunningJobs}
              downloadInitiatedRef={downloadInitiatedRef}
            />
          </Grid>
        }
      />
      <Route
        path="activity"
        element={
          <Grid container spacing={2}>
            <DownloadProgress
              downloadProgressRef={downloadProgressRef}
              downloadInitiatedRef={downloadInitiatedRef}
              jobs={jobs}
              token={token}
            />
          </Grid>
        }
      />
      <Route
        path="history"
        element={
          <Grid container spacing={2}>
            <DownloadHistory
              jobs={jobs}
              expanded={expanded}
              handleExpandCell={handleExpandCell}
              currentTime={currentTime}
              isMobile={isMobile}
              token={token}
              onVideoDeleted={fetchRunningJobs}
            />
          </Grid>
        }
      />
      <Route path="*" element={<Navigate to="manual" replace />} />
    </Routes>
  );
}

export default DownloadManager;
